import React, { useCallback, useEffect, useState } from "react";
import { Badge, IconButton, Popover, Tooltip } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationList from "./NotificationList";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { useUser } from "../../context/UserContext";
import { getCurrentESTTime } from "../../utils/dateHelper";
import { INTERNALMESSAGES } from "../../constants/messages";
import useApi from "../../hooks/useApi";
import { getMessagesUnreadCount } from "../../services/messageService";
import { GLOBALSETTINGS, Messages } from "../../data/constants";
import { playAlertSound } from "../../utils/commonHelper";
import { useSignalR } from "../../hooks/useSignalR";
import "./Notifications.scss";
import { useSignalRConnections } from "../../context/SignalRProvider";

const NotificationPopover: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { userId } = useUser();
  const [notificationCount, setNotificationCount] = useState(0);
  const [shouldBlink, setShouldBlink] = useState(false);
  const { executeApi } = useApi();

  const notificationAlertHandler = useCallback(
    (toUserIds: number[]) => {
      if (toUserIds.includes(userId)) {
        setNotificationCount((prev) => prev + 1);
        playAlertSound();
        setShouldBlink(true);
        setTimeout(() => setShouldBlink(false), GLOBALSETTINGS.BLINK_TIME);
      }
    },
    [userId]
  );

  const notificationFolderDuplicateHandler = useCallback(
    (notifications: { toUserId: number; totalRecords: number }[]) => {
      const match = notifications.find((n) => n.toUserId === userId);
      if (match) {
        setNotificationCount((prev) => prev + match.totalRecords);
        playAlertSound();
        setShouldBlink(true);
        setTimeout(() => setShouldBlink(false), GLOBALSETTINGS.BLINK_TIME);
      }
    },
    [userId]
  );

  const { notificationConnection } = useSignalRConnections();

  useSignalR(notificationConnection, "NotificationAlert", notificationAlertHandler);
  useSignalR(notificationConnection, "NotificationFolderAlert", notificationFolderDuplicateHandler);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    logAuditTrace({
      userId: userId,
      objectType: "Page",
      objectId: null,
      objectName: `Navigated to Notification`,
      action: "Navigated",
      navigationDateTime: getCurrentESTTime(),
    });
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchNotificationsCount = useCallback(async () => {
    if (userId && userId > 0) {
      const result = await executeApi<void, number>(getMessagesUnreadCount, null);
      if (result) {
        setNotificationCount(result);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchNotificationsCount();
  }, [fetchNotificationsCount]);

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Tooltip title={INTERNALMESSAGES.NOTIFICATIONS}>
          <Badge
            badgeContent={notificationCount}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "red",
                color: "white",
              },
            }}
            max={Messages.MAXIMUMCOUNT_LIMIT}
          >
            <NotificationsIcon id="notification" className={shouldBlink ? "blink" : ""} />
          </Badge>
        </Tooltip>
      </IconButton>

      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: { marginTop: 8 },
          },
        }}
      >
        <NotificationList onItemClick={handleClose} onCountChange={setNotificationCount} />
      </Popover>
    </>
  );
};

export default NotificationPopover;
